import React, { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";

function safeString(v) {
  return typeof v === "string" ? v : v == null ? "" : String(v);
}

function normalizeRole(v) {
  const raw = safeString(v).trim().toLowerCase().replace(/[_-]+/g, " ");

  if (raw === "tech") return "technician";
  if (raw === "service manager" || raw === "store manager") return "manager";
  if (raw === "service advisor") return "advisor";
  if (raw === "parts advisor" || raw === "parts staff") return "staff";
  if (raw === "parts manager") return "parts";
  if (raw === "shop foreman") return "foreman";

  return raw;
}

function getStoreLandingPath(role) {
  const normalized = normalizeRole(role);

  if (normalized === "advisor") return "/advisor-log";
  if (normalized === "parts" || normalized === "staff") return "/parts";
  if (normalized === "technician") return "/tech";
  if (normalized === "foreman") return "/shop-foreman";
  if (normalized === "dispatcher") return "/dispatch";

  return "/dashboard";
}

export default function SelectStorePage() {
  const auth = useAuth();

  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const activeStoreId = safeString(
    auth?.effectiveStoreId || auth?.activeStoreId
  ).trim();

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setErr("");
      setLoading(true);

      try {
        if (!auth?.token) throw new Error("Not signed in.");

        const data = await auth.apiGet("/api/auth/my-stores");
        const list = Array.isArray(data?.stores) ? data.stores : [];

        if (!cancelled) setStores(list);
      } catch (e) {
        if (!cancelled) {
          setErr(e?.message || "Failed to load your stores");
          setStores([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [auth]);

  function pickStore(storeId) {
    const id = safeString(storeId).trim();
    if (!id) return;

    const selectedStore =
      stores.find((row) => safeString(row?.storeId).trim() === id) || null;

    if (typeof auth?.setActiveStoreId === "function") {
      auth.setActiveStoreId(id);
    }

    if (typeof auth?.setStoreMode === "function") {
      auth.setStoreMode();
    }

    // Full navigation is intentional so every store-scoped context reloads cleanly
    // under the newly selected X-Store-Id.
    window.location.assign(getStoreLandingPath(selectedStore?.role));
  }

  return (
    <div className="ft-page ft-page--pad">
      <div className="ft-header">
        <div className="ft-header__text">
          <h1 className="ft-title">Select Business Unit</h1>
          <p className="ft-subtitle">
            Choose which store you want to view right now.
          </p>
        </div>
      </div>

      {err ? (
        <div className="ft-card ft-card--error">
          <div className="ft-errorText">{err}</div>
        </div>
      ) : null}

      <div className="ft-card">
        <div className="ft-card__body">
          {loading ? (
            <div className="ft-muted">Loading…</div>
          ) : stores.length === 0 ? (
            <div className="ft-muted">
              No stores found for this account. You may not have an active
              employment yet.
            </div>
          ) : (
            <div style={{ display: "grid", gap: 10 }}>
              {stores.map((s) => {
                const storeId = safeString(s.storeId).trim();
                const storeName = safeString(s.storeName).trim();
                const storeCode = safeString(s.storeCode).trim();
                const groupName = safeString(s.groupName).trim();
                const role = safeString(s.role).trim();
                const status = safeString(s.status).trim().toUpperCase();
                const isCurrent = storeId === activeStoreId;

                const label =
                  groupName &&
                  storeName &&
                  groupName.toLowerCase() !== storeName.toLowerCase()
                    ? `${groupName} / ${storeName}`
                    : storeName || storeCode || storeId.slice(0, 8);

                const disabled = status && status !== "ACTIVE";

                return (
                  <button
                    key={storeId}
                    type="button"
                    className="ft-rowBtn"
                    onClick={() => pickStore(storeId)}
                    disabled={disabled}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: 12,
                      padding: 12,
                      borderRadius: 12,
                      border: isCurrent
                        ? "1px solid rgba(37,99,235,0.30)"
                        : "1px solid rgba(0,0,0,0.10)",
                      background: isCurrent ? "#eff6ff" : "#fff",
                      textAlign: "left",
                      cursor: disabled ? "not-allowed" : "pointer",
                    }}
                  >
                    <div style={{ display: "grid", gap: 3, minWidth: 0 }}>
                      <div style={{ fontWeight: 800 }}>{label}</div>
                      <div className="ft-subtle" style={{ fontSize: 12 }}>
                        {[storeCode, role].filter(Boolean).join(" • ") || "Store access"}
                      </div>
                    </div>

                    <div
                      style={{
                        display: "flex",
                        gap: 8,
                        alignItems: "center",
                        flexWrap: "wrap",
                        justifyContent: "flex-end",
                      }}
                    >
                      {isCurrent ? <span className="ft-pill">Current</span> : null}
                      <span className="ft-pill">{role || "staff"}</span>
                      <span
                        className={`ft-pill ft-pill--status ${
                          !disabled ? "is-active" : "is-inactive"
                        }`}
                      >
                        {!disabled ? "active" : "inactive"}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div style={{ marginTop: 10 }} className="ft-subtle">
        You can switch stores later from the sidebar without logging out.
      </div>

    </div>
  );
}
